// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import core from 'frontcore/index'
import VueI18n, { createI18n } from 'vue-i18n'
import { _get } from 'frontcore/utils/communications/apiClient'
import { computed } from 'vue'
import { I18N_URL } from 'frontcore/constants/communications'

const getDefaultUserLanguage = (): string => {
  if (window.navigator.languages) { return window.navigator.languages[0] }
  return window.navigator.language
}

const getDefaultUserRegion = (): string => {
  const [language, region] = getDefaultUserLanguage().split('-')
  if (language !== 'en' && !region) { return 'GB' }
  return region
}

export const FALLBACK_LANGUAGE = 'en'
const DEFAULT_LANGUAGE = getDefaultUserLanguage().split('-')[0]
export const DEFAULT_REGION = getDefaultUserRegion()
const LANGUAGE_KEY = 'lang'

export const LANGUAGE_STRINGS: Record<string, string> = {
  'en': 'languages.messages.english',
  'ca': 'languages.messages.catalan',
  'cs': 'languages.messages.czech',
  'de': 'languages.messages.german',
  'es': 'languages.messages.spanish',
  'fr': 'languages.messages.french',
  'hr': 'languages.messages.croatian',
  'hu': 'languages.messages.hungarian',
  'it': 'languages.messages.italian',
  'ja': 'languages.messages.japanese',
  'ko': 'languages.messages.korean',
  'lt': 'languages.messages.lithuanian',
  'nl': 'languages.messages.dutch',
  'pl': 'languages.messages.polish',
  'pt': 'languages.messages.portuguese',
  'ro': 'languages.messages.romanian',
  'ru': 'languages.messages.russian',
  'sk': 'languages.messages.slovak',
  'sr': 'languages.messages.serbian',
  'th': 'languages.messages.thai',
  'tr': 'languages.messages.turkish',
  'zh': 'languages.messages.chinese',
}

export const UNTRANSLATED_LANGUAGE_STRINGS: Record<string, string> = {
  
  'en': 'English',
  'ca': 'Català',
  'cs': 'Čeština',
  'de': 'Deutsch',
  'es': 'Español',
  'fr': 'Français',
  'hr': 'Hrvatski',
  'hu': 'Magyar',
  'it': 'Italiano',
  'ja': '日本語',
  'ko': '한국인',
  'lt': 'Lietuvių',
  'nl': 'Neerlandés',
  'pl': 'Polski',
  'pt': 'Português',
  'ro': 'Română',
  'ru': 'Русский',
  'sk': 'Slovenský',
  'sr': 'Српски',
  'th': 'แบบไทย',
  'tr': 'Türkçe',
  'zh': '中文',
}

export const ALL_LANGUAGES: string[] = [
  'en', 'ca', 'cs', 'de', 'es', 'fr', 'hu', 'hr', 'it', 'ja', 'ko',  'lt', 'nl', 'pl', 'pt', 'ro', 'ru', 'sk', 'sr', 'th', 'tr', 'zh',      
]

export const setLanguage = (language: string = getLanguage()) => {
  core.setMomentLocale(language)
  document.getElementsByTagName('html')[0].setAttribute('lang', language.substring(0, 2))
  localStorage.setItem(LANGUAGE_KEY, language)
  i18n.global.locale = language
}

export const getLanguage = (): string => {
  const language = localStorage.getItem(LANGUAGE_KEY)
  const baseLanguage = LANGUAGE_STRINGS[DEFAULT_LANGUAGE] ? DEFAULT_LANGUAGE : FALLBACK_LANGUAGE
  if (!language) {
    setLanguage(baseLanguage)
  }
  return language ?? baseLanguage
}

export const getLocaleLanguage = (): string[] => {
  const language = localStorage.getItem(LANGUAGE_KEY)
  const locale = [`${language}-${DEFAULT_REGION}`]
  if (language) {
    locale.push(language)
  }
  return [
    ...locale,
    `${DEFAULT_LANGUAGE}-${DEFAULT_REGION}`,
    DEFAULT_LANGUAGE,
    FALLBACK_LANGUAGE,
  ]
}

const i18n = createI18n(
  {
    locale: 'en',
    messages: {},
  }, VueI18n
)
const t = i18n.global.t
const tc = i18n.global.tc

i18n.global.t = (key: Path | number, named: NamedValue = {}, isRequired = false) => {
  if (key.split('.').length !== 3) { return key }
  const result = t(key, named)
  return `${result}${isRequired ? '*' : ''}`
}
i18n.global.tc = (key: string, plural: number, named: NamedValue = {}, isRequired = false) => {
  if (key.split('.').length !== 3) { return key }
  const result = tc(key, plural, named)
  return `${result}${isRequired ? '*' : ''}`
}

export const getLocalizedLanguage = (languageKey: string): string => {
  return i18n.global.t(LANGUAGE_STRINGS[languageKey])
}

export const setupI18n = async (): Promise<void> => {
  const endpoint = `${I18N_URL}/?lang=${getLanguage()}`
  const tokens = await _get(endpoint) as Record<string, Record<string, string>>
  Object.entries(tokens).forEach(([locale, messages]) => {
    i18n.global.setLocaleMessage(locale, messages)
  })
  setLanguage()
}

export const monthNames = computed(() => {
  const names: string[] = []
  const date = new Date()
  date.setDate(1)
  for (let i=0; i<12; i++) {
    date.setMonth(i)
    names.push(date.toLocaleString(getLanguage(), {
      month: 'short'}))
  }
  return names
})

export const getMonthShortName = (index: number): string => {
  const date = new Date()
  date.setDate(1)
  date.setMonth(index)
  return date.toLocaleString(getLanguage(), { month: 'short' })
}

export default i18n
